import { of, empty, concat } from "rxjs";
import {
    filter,
    mergeMap,
    withLatestFrom,
    map,
    switchMap,
    tap,
    catchError,
    mapTo,
    mergeMapTo,
} from "rxjs/operators";
import { combineEpics, ofType } from "redux-observable";
import * as ajax from "../../common/services/utils";
import actionCreators from "./actionCreators";
import { default as UIActionCreators } from "../MainMenu/actionCreators";
import errorHandler from "../../common/services/ajaxErrorHandler";
// import { logoutUrl } from "../../common/services/saml";
import { push } from "connected-react-router";
import { switchCase } from "../../common/services/utils";

// (almost) all reducers
import { default as AutoCompleteActionCreators } from "../AutoComplete/actionCreators";
import { default as CalendarAndEventsActionCreators } from "../CalendarAndEvents/actionCreators";
import { default as CampusMappingActionCreators } from "../CampusMapping/actionCreators";
import { default as ChannelsEventsActionCreators } from "../Channels/Events/actionCreators";
import { default as ChannelsGroupsActionCreators } from "../Channels/Groups/actionCreators";
import { default as ChannelsGroupsMembersActionCreators } from "../Channels/Groups/Members/actionCreators";
import { default as ChannelsPagesActionCreators } from "../Channels/Pages/actionCreators";
import { default as ChannelsPagesFollowersActionCreators } from "../Channels/Pages/Followers/actionCreators";
import { default as ChannelsPostsActionCreators } from "../Channels/Posts/actionCreators";
import { default as ChannelsActionCreators } from "../Channels/actionCreators";
import { default as DashboardActionCreators } from "../Dashboard/actionCreators";
import { default as DiscussionActionCreators } from "../Discussion/actionCreators";
import { default as FeedbackActionCreators } from "../Feedback/actionCreators";
import { default as FroalaActionCreators } from "../Froala/actionCreators";
import { default as NotificationActionCreators } from "../Notification/actionCreators";
import { default as NotificationBannerActionCreators } from "../NotificationBanner/actionCreators";
import { default as OfficeHoursAcademicsActionCreators } from "../OfficeHours/Academics/actionCreators";
import { default as OfficeHoursAdministratorsActionCreators } from "../OfficeHours/Administrators/actionCreators";
import { default as OfficeHoursAppointmentsActionCreators } from "../OfficeHours/Appointments/actionCreators";
import { default as OfficeHoursAvailabilitiessActionCreators } from "../OfficeHours/Availabilities/actionCreators";
import { default as OfficeHoursReportsActionCreators } from "../OfficeHours/Reports/actionCreators";
import { default as OfficeHoursSessionsActionCreators } from "../OfficeHours/Sessions/actionCreators";
import { default as OfficeHoursTeamsActionCreators } from "../OfficeHours/Teams/actionCreators";
import { default as PersistentEventsActionCreators } from "../PersistentEvents/actionCreators";
import { default as SettingsSchoolYearsActionCreators } from "../Settings/SchoolYears/actionCreators";
import { default as SideLinksActionCreators } from "../SideLinks/actionCreators";
import { default as SnackbarActionCreators } from "../Snackbar/actionCreators";
import { default as SupportPageActionCreators } from "../SupportPage/actionCreators";
import { default as UsersAndAudiencesAudiencesActionCreators } from "../UsersAndAudiences/Audiences/actionCreators";
import { default as UsersAndAudiencesLseStaffActionCreators } from "../UsersAndAudiences/LseStaff/actionCreators";
import { default as UsersAndAudiencesLseStudentsActionCreators } from "../UsersAndAudiences/LseStudents/actionCreators";

const authStartLoadingEpic = action$ =>
    action$.pipe(
        ofType(actionCreators.checkStorage.type),
        mapTo(UIActionCreators.setLoading.create())
    );

const authClearLoadingEpic = action$ =>
    action$.pipe(
        ofType(
            actionCreators.login.type,
            actionCreators.errorResponse.type,
            actionCreators.updateCurrentUser.type,
            actionCreators.setNotAuth.type
        ),
        mapTo(UIActionCreators.clearLoading.create())
    );

const samlValidateEpic = action$ =>
    action$.pipe(
        ofType(actionCreators.samlValidate.type),
        switchMap(({ payload }) =>
            ajax
                .post(ajax.apiUrl("auth/v2/backend/exchange-code"), payload, {
                    "Content-Type": "application/json",
                })
                .pipe(
                    tap(({ response }) => {
                        localStorage.setItem("token", response.id);
                        localStorage.removeItem("lsha_auth_state");
                        localStorage.removeItem("lsha_auth_code_verifier");
                        window.location = "/";
                    }),
                    mergeMapTo(empty()),
                    catchError(errorHandler(actionCreators.errorResponse.create))
                )
        )
    );

const restoreUserEpic = (action$, state$) =>
    action$.pipe(
        ofType(actionCreators.restoreUser.type),
        withLatestFrom(state$),
        map(([, { Auth }]) => {
            const token = localStorage.getItem("token");
            return [token, Auth];
        }),
        filter(([token, Auth]) => token && Auth.token !== token),
        mergeMap(([token]) =>
            of(actionCreators.setAuth.create({ token }), actionCreators.getCurrentUser.create())
        )
    );

const checkStorageEpic = action$ =>
    action$.pipe(
        ofType(actionCreators.checkStorage.type),

        mergeMap(() => {
            const token = localStorage.getItem("token");
            const isMasqueraded = localStorage.getItem("isMasqueraded");
            if (token) {
                return concat(
                    of(actionCreators.login.create()),
                    of(actionCreators.setMasqueraded.create(!!isMasqueraded))
                );
            } else {
                return of(actionCreators.setInitiated.create());
            }
        })
    );

const getCurrentUserEpic = action$ =>
    action$.pipe(
        ofType(actionCreators.getCurrentUser.type),
        switchMap(() =>
            ajax.get(ajax.apiUrl(`staff/profile`, import.meta.env)).pipe(
                mergeMap(({ response }) => of(actionCreators.updateCurrentUser.create(response))),
                catchError(res => {
                    const handleError = errorHandler(actionCreators.errorResponse.create);
                    let errorResponse = handleError(res);
                    errorResponse = concat(errorResponse, of(actionCreators.setInitiated.create()));
                    return errorResponse;
                })
            )
        )
    );

const loginEpic = action$ =>
    action$.pipe(
        ofType(actionCreators.login.type),
        mergeMap(() => {
            return concat(
                of(actionCreators.getCurrentUser.create()),
                of(actionCreators.clearAllReducers.create())
            );
        })
    );

const logoutEpic = action$ =>
    action$.pipe(
        ofType(actionCreators.logout.type),
        mergeMap(() => {
            const savedToken = localStorage.getItem("savedToken");
            if (savedToken) {
                localStorage.setItem("token", savedToken);
                localStorage.removeItem("isMasqueraded");
                localStorage.removeItem("savedToken");
                return concat(
                    of(actionCreators.setAuth.create({ token: savedToken })),
                    of(actionCreators.logoutMasquaraded.create())
                );
            } else {
                localStorage.removeItem("token");
                return concat(
                    of(actionCreators.setMasqueraded.create(false)),
                    of((window.location.href = "/logout"))
                );
            }
        })
    );

const logoutMasquaradedEpic = action$ =>
    action$.pipe(
        ofType(actionCreators.logoutMasquaraded.type),
        mergeMap(() => {
            return concat(
                of(actionCreators.getCurrentUser.create()),
                of(actionCreators.clearAllReducers.create()),
                of(actionCreators.setMasqueraded.create(false)),
                of(push("/users-and-audiences/lse-staff"))
            );
        })
    );

const interactiveLoginEpic = action$ =>
    action$.pipe(
        ofType(actionCreators.interactiveLogin.type),
        mergeMap(action =>
            ajax
                .post(
                    ajax.apiUrl("auth/interactive-login"),
                    { token: action.payload.token },
                    {
                        "Content-Type": "application/json",
                    }
                )
                .pipe(
                    map(res => ({ data: res.response, status: res.status })),
                    mergeMap(res => {
                        if (res.status === 200) {
                            localStorage.setItem("savedToken", action.payload.oldToken);
                            localStorage.setItem("token", res.data.id);
                            localStorage.setItem("isMasqueraded", 1);
                        }

                        return switchCase({
                            200: concat(
                                of(push("/")),
                                of(actionCreators.setAuth.create({ token: res.data.id })),
                                of(actionCreators.loginMasquaraded.create())
                            ),
                            204: of(
                                actionCreators.errorResponse.create({
                                    errorRes:
                                        "Something seems to have gone wrong - we're experiencing some technical difficulties. Please try again later!",
                                })
                            ),
                        })()(res.status);
                    }),
                    catchError(errorHandler(actionCreators.errorResponse.create))
                )
        )
    );

const loginMasquaradedEpic = action$ =>
    action$.pipe(
        ofType(actionCreators.loginMasquaraded.type),
        mergeMap(() => {
            return concat(
                of(actionCreators.getCurrentUser.create()),
                of(actionCreators.setMasqueraded.create(true)),
                of(actionCreators.clearAllReducers.create())
            );
        })
    );

const clearAllReducersEpic = action$ =>
    action$.pipe(
        ofType(actionCreators.clearAllReducers.type),
        mergeMap(() => {
            return concat(
                // reset (almost) all reducers - new users needs to load all data from the scratch
                of(AutoCompleteActionCreators.reset.create()),
                of(CalendarAndEventsActionCreators.reset.create()),
                of(CampusMappingActionCreators.reset.create()),
                of(ChannelsEventsActionCreators.reset.create()),
                of(ChannelsGroupsActionCreators.reset.create()),
                of(ChannelsGroupsMembersActionCreators.reset.create()),
                of(ChannelsPagesActionCreators.reset.create()),
                of(ChannelsPagesFollowersActionCreators.reset.create()),
                of(ChannelsPostsActionCreators.reset.create()),
                of(ChannelsActionCreators.reset.create()),
                of(DashboardActionCreators.reset.create()),
                of(DiscussionActionCreators.reset.create()),
                of(FeedbackActionCreators.reset.create()),
                of(FroalaActionCreators.reset.create()),
                of(NotificationActionCreators.reset.create()),
                of(NotificationBannerActionCreators.reset.create()),
                of(OfficeHoursAcademicsActionCreators.reset.create()),
                of(OfficeHoursAdministratorsActionCreators.reset.create()),
                of(OfficeHoursAppointmentsActionCreators.reset.create()),
                of(OfficeHoursAvailabilitiessActionCreators.reset.create()),
                of(OfficeHoursReportsActionCreators.reset.create()),
                of(OfficeHoursSessionsActionCreators.reset.create()),
                of(OfficeHoursTeamsActionCreators.reset.create()),
                of(PersistentEventsActionCreators.reset.create()),
                of(SettingsSchoolYearsActionCreators.reset.create()),
                of(SideLinksActionCreators.reset.create()),
                of(SnackbarActionCreators.reset.create()),
                of(SupportPageActionCreators.reset.create()),
                of(UsersAndAudiencesAudiencesActionCreators.reset.create()),
                of(UsersAndAudiencesLseStaffActionCreators.reset.create()),
                of(UsersAndAudiencesLseStudentsActionCreators.reset.create())
            );
        })
    );

export const epics = combineEpics(
    authStartLoadingEpic,
    authClearLoadingEpic,
    samlValidateEpic,
    checkStorageEpic,
    restoreUserEpic,
    getCurrentUserEpic,
    logoutEpic,
    loginEpic,
    interactiveLoginEpic,
    logoutMasquaradedEpic,
    loginMasquaradedEpic,
    clearAllReducersEpic
);
